import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async'; // Added for SEO
import api from "../../api/axios";

const ContactContent = () => {
  const colors = { primary: "#ffaa00", secondary: "#8A8A89" };

  // 1. Form State
  const [formData, setFormData] = useState({
    name: '',
    subject: 'SAV / Réparation',
    message: ''
  });

  // 2. UI State
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  // 3. Handle Input Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 4. Submit to Laravel
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await api.post('/contact', formData);
      if (response.data.success) {
        setStatus({ 
          type: 'success', 
          message: 'Votre message a été envoyé avec succès !' 
        });
        setFormData({ name: '', subject: 'SAV / Réparation', message: '' });
      }
    } catch (error) {
      console.error("Contact Error:", error);
      setStatus({ 
        type: 'error', 
        message: "Une erreur est survenue. Veuillez réessayer plus tard." 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
      {/* --- SEO IMPLEMENTATION --- */}
      <Helmet>
        <title>Contactez Electro Titan | SAV & Support Électroménager Salé</title>
        <meta 
          key="description" name="description" 
          content="Contactez l'équipe Electro Titan à Salé. Support technique, service après-vente (SAV), et informations sur nos équipements électriques. Situé à Tabriquet, Salé." 
        />
        
        {/* Schema.org Local Business & Contact Info */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Electro Titan",
            "image": "https://www.titanelectro.ma/assets/images/logo.png",
            "telephone": "+212640033322",
            "email": "contact@titanelectro.ma",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "8, Avenue allal al arbi al alaoui Hay Salam",
              "addressLocality": "Tabriquet",
              "addressRegion": "Salé",
              "addressCountry": "MA"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+212640033322",
              "contactType": "customer service",
              "areaServed": "MA",
              "availableLanguage": ["French", "Arabic"]
            }
          })}
        </script>
      </Helmet>

      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-12">
          <h1 className="text-4xl font-black uppercase tracking-tighter">Contactez-nous</h1>
          <div className="h-1.5 w-20 mt-2" style={{ backgroundColor: colors.primary }}></div>
          <p className="text-[#8A8A89] font-bold mt-4 uppercase text-xs tracking-widest">
            Une question ? Notre équipe Electro Titan vous répond.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* LEFT: Contact Information */}
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100">
                <Phone style={{ color: colors.primary }} size={24} className="mb-4" />
                <h3 className="font-black text-sm uppercase tracking-widest mb-1">Téléphone</h3>
                <Link to={"tel:+212640033322"} className="hover:text-[#ffaa00] transition-colors">
                  <p className="text-gray-600 font-bold">+212 6 40 03 33 22</p>
                </Link>
              </div>
              <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100">
                <Mail style={{ color: colors.primary }} size={24} className="mb-4" />
                <h3 className="font-black text-sm uppercase tracking-widest mb-1">Email</h3>
                <a href="mailto:contact@titanelectro.ma" className="text-gray-600 font-bold hover:text-[#ffaa00]">
                   contact@titanelectro.ma
                </a>
              </div>
            </div>

            <div className="p-8 bg-[#8A8A89] rounded-[2.5rem] text-white relative overflow-hidden">
              <div className="relative z-10">
                <MapPin style={{ color: colors.primary }} size={32} className="mb-4" />
                <h2 className="text-2xl font-black uppercase mb-2">Notre Boutique</h2>
                <address className="not-italic text-white font-medium mb-6">
                  8, Avenue allal al arbi al alaoui Hay Salam,<br />
                  Tabriquet, Salé, Maroc
                </address>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b border-white/10 pb-2">
                    <span className="text-xs uppercase font-bold text-white">Lun - Ven</span>
                    <span className="text-xs">09:00 - 19:00</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-white/10 pb-2">
                    <span className="text-xs uppercase font-bold text-white">Samedi</span>
                    <span className="text-xs">09:00 - 15:00</span>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full opacity-10" style={{ backgroundColor: colors.primary }}></div>
            </div>
          </div>

          {/* RIGHT: Contact Form */}
          <div className="bg-white p-8 md:p-10 rounded-[2.5rem] border border-gray-100 shadow-2xl">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {status.message && (
                <div className={`p-4 rounded-xl text-xs font-bold uppercase tracking-widest ${status.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {status.message}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#8A8A89]">Nom Complet</label>
                  <input 
                    type="text" 
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full p-4 bg-gray-50 rounded-2xl border-none focus:ring-2 focus:ring-[#ffaa00] outline-none transition-all font-bold text-sm"
                    placeholder="Votre nom"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#8A8A89]">Sujet</label>
                  <select 
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full p-4 bg-gray-50 rounded-2xl border-none focus:ring-2 focus:ring-[#ffaa00] outline-none transition-all font-bold text-sm cursor-pointer"
                  >
                    <option>SAV / Réparation</option>
                    <option>Suivi de commande</option>
                    <option>Information produit</option>
                    <option>Autre</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#8A8A89]">Message</label>
                <textarea 
                  name="message"
                  required
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full p-4 bg-gray-50 rounded-2xl border-none focus:ring-2 focus:ring-[#ffaa00] outline-none transition-all font-bold text-sm"
                  placeholder="Comment pouvons-nous vous aider ?"
                ></textarea>
              </div>

              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
                className="w-full py-5 bg-[#ffaa00] text-white rounded-2xl font-black uppercase tracking-[0.3em] text-xs shadow-lg shadow-orange-100 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>Patientez... <Loader2 size={16} className="animate-spin" /></>
                ) : (
                  <>Envoyer le Message <Send size={16} /></>
                )}
              </motion.button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactContent;